import { useState, useMemo } from "react";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "../constants/pagination";

// Server-side pagination hook interface
export interface UseServerPaginationReturn {
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  totalItems: number;
  handlePageChange: (pageNumber: number) => void;
  handleItemsPerPageChange: (newSize: string | number) => void;
  getQueryParams: Record<string, any>;
}

/**
 * Server-side pagination hook for API calls with page and limit parameters
 * @param totalItems - Total number of items from server response
 * @param initialPage - Initial page number (default from constants)
 * @param initialPageSize - Initial page size (default from constants)
 * @returns Server pagination state and handlers
 */
export const useServerPagination = (
  totalItems: number = 0,
  initialPage: number = DEFAULT_PAGE,
  initialPageSize: number = DEFAULT_PAGE_SIZE
): UseServerPaginationReturn => {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialPageSize);

  const totalPages = useMemo<number>(() => {
    return Math.ceil(totalItems / itemsPerPage);
  }, [totalItems, itemsPerPage]);

  const handlePageChange = (pageNumber: number): void => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleItemsPerPageChange = (newSize: string | number): void => {
    setItemsPerPage(parseInt(newSize.toString()));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const getQueryParams = useMemo((): Record<string, any> => {
    return {
      page: currentPage,
      limit: itemsPerPage,
    };
  }, [currentPage, itemsPerPage]);

  return {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    handlePageChange,
    handleItemsPerPageChange,
    getQueryParams,
  };
};
